import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  signal,
  ViewChild,
  effect,
  OnDestroy,
  Renderer2,
  ChangeDetectorRef,
  AfterViewInit,
  afterNextRender,
  Injector,
  runInInjectionContext,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { DropdownService } from 'src/utils/dropdown.service';
import { CustomTooltipDirective } from '../../../directives/custom-tooltip.directive';
import { CheckboxComponent } from '../../checkbox/checkbox.component';

export interface SearchableOption {
  value: string | number;
  label: string;
  options?: SearchableOption[];
  isGroup?: boolean;
  isStrong?: boolean;
}
const OPEN_GUARD_MS = 100;
@Component({
  selector: 'searchable-dropdown',
  imports: [CommonModule, FormsModule, CustomTooltipDirective, CheckboxComponent],
  templateUrl: './searchable-dropdown.component.html',
  standalone: true,
})
export class SearchableDropdownComponent implements OnDestroy, AfterViewInit {
  private _options: SearchableOption[] = [];
  private lastSelectedValue: string | null = null;
  private pendingValue: string | null = null;

  @Input() loading: boolean = false;
  @Input() dropdownId?: string;

  @Input()
  set options(v: SearchableOption[]) {
    const oldOptions = [...this._options];
    this._options = v || [];

    const optionsChanged = this.hasOptionsContentChanged(
      oldOptions,
      this._options
    );

    if (this.pendingValue && this._options.length > 0) {
      const found = this.findOptionByValue(this._options, this.pendingValue);
      if (found) {
        this._selectedOption = found;
        this.searchTerm = '';
        this.pendingValue = null;
        this.cd.detectChanges();
        return;
      }
    }

    if (optionsChanged && this.lastSelectedValue && this._options.length > 0) {
      const found = this.findOptionByValue(
        this._options,
        this.lastSelectedValue
      );
      if (found) {
        this._selectedOption = found;
        this.searchTerm = '';
        this.cd.detectChanges();
        return;
      }
    }

    if (this._selectedOption) {
      const exists = !!this.findOptionByValue(
        this._options,
        this._selectedOption.value
      );
      if (!exists) {
        this._selectedOption = null;
        this.searchTerm = '';
      } else {
        const updatedOption = this.findOptionByValue(
          this._options,
          this._selectedOption.value
        );
        if (updatedOption) {
          this._selectedOption = updatedOption;
        }
      }
    }

    if (optionsChanged) {
      this.searchTerm = '';
    }

    this.cd.detectChanges();
  }

  get options(): SearchableOption[] {
    return this._options;
  }

  @Input() icon: string = 'fi fi-rr-filter';
  @Input() placeholder: string = 'Seleccionar...';
  @Input() allowCreateFromInput: boolean = true;
  @Input() disabled: boolean = false;
  @Input() multiSelect: boolean = false;
  @Input() autoOpen: boolean = false;
  @Input() hideTrigger: boolean = false;
  @Output() inputChanged = new EventEmitter<string | null>();
  @Output() onDataChanged = new EventEmitter<string | null>();
  @Output() multiSelectChanged = new EventEmitter<SearchableOption[]>();
  @Output() closed = new EventEmitter<void>();

  private _selectedOption: SearchableOption | null = null;
  private _selectedOptions: SearchableOption[] = [];

  private findOptionByValue(
    options: SearchableOption[],
    value: string | number
  ): SearchableOption | null {
    for (const option of options) {
      if (String(option.value) === String(value)) {
        return option;
      }
      if (option.options && option.options.length > 0) {
        const found = this.findOptionByValue(option.options, value);
        if (found) return found;
      }
    }
    return null;
  }

  @Input()
  set selectedOption(val: string | null) {
    if (!val) {
      this.searchTerm = '';
      this._selectedOption = null;
      this.lastSelectedValue = null;
      this.pendingValue = null;
      return;
    }

    this.lastSelectedValue = val;

    const found = this.findOptionByValue(this._options, val);
    const exists = !!found;

    if (exists) {
      this._selectedOption = found;
      this.searchTerm = '';
      this.pendingValue = null;
      this.cd.detectChanges();
      return;
    } else {
      this.pendingValue = val;
      this._selectedOption = null;
      this.searchTerm = '';

      setTimeout(() => {
        if (this.pendingValue) {
          const retryFound = this.findOptionByValue(
            this._options,
            this.pendingValue
          );
          if (retryFound) {
            this._selectedOption = retryFound;
            this.searchTerm = '';
            this.pendingValue = null;
            this.cd.detectChanges();
          }
        }
      }, 300);

      return;
    }
  }

  get selectedOption(): SearchableOption | null {
    return this._selectedOption;
  }

  @Input() dropdownHeight: string = '42px';
  @Input() dropdownBorderRadius: string = '8px';
  @Input() fontSize: string = '16px';
  @Input() fontWeight: string = '500';
  @Input() placeholderFontSize: string = '16px';
  @Input() placeholderFontWeight: string = '500';
  @Input() variant: 'filled' | 'outlined' = 'filled';
  @Input() invalid: boolean = false;
  @Input() panelWidth?: string | null = '216px';
  @Input() attachToBody: boolean = true;

  get selectedOptions(): SearchableOption[] {
    return this._selectedOptions;
  }

  @Input()
  set selectedOptions(val: SearchableOption[]) {
    this._selectedOptions = val || [];
  }

  private readonly searchTermSubject = new BehaviorSubject<string>('');
  searchTerm$ = this.searchTermSubject.asObservable();
  isOpen = signal(false);

  @ViewChild('selectContainer', { static: false }) selectContainer!: ElementRef;
  @ViewChild('panel', { static: false }) panelRef!: ElementRef<HTMLDivElement>;

  private placeholderNode?: Comment | null = null;
  moved = false;
  private readonly onWindowResize = () => {
    this.dropdownService.closeDropdown();
  };
  private readonly onWindowScroll = () => {
    this.positionPanel();
  };
  private readonly onAncestorScroll = () => {
    this.dropdownService.closeDropdown();
  };
  private scrollableAncestors: HTMLElement[] = [];
  private initTime = 0;
  private resizeObserver?: ResizeObserver;
  private layoutObserver?: ResizeObserver;

  private readonly injector = inject(Injector);

  constructor(
    private readonly dropdownService: DropdownService,
    private readonly renderer: Renderer2,
    private readonly host: ElementRef,
    private readonly cd: ChangeDetectorRef
  ) {
    if (!this.dropdownId) {
      this.dropdownId = `searchable-${Math.random().toString(36).substr(2, 9)}`;
    }

    effect(() => {
      const openDropdownId = this.dropdownService.getOpenDropdown()();
      const wasOpen = this.isOpen();

      if (openDropdownId === this.dropdownId) {
        this.isOpen.set(true);
      } else {
        this.isOpen.set(false);
        if (!this.multiSelect) {
          this.searchTerm = '';
        }
        if (wasOpen) {
          this.closed.emit();
        }
      }
    });

    effect(() => {
      if (this.isOpen()) {
        runInInjectionContext(this.injector, () => {
          afterNextRender(() => this.openPanel());
        });
      } else {
        this.closePanel();
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.autoOpen && !this.disabled && !this.hideTrigger) {
      this.initTime = Date.now();
      setTimeout(() => {
        this.dropdownService.toggleDropdown(this.dropdownId!);
      }, 10);
    }
  }

  private hasOptionsContentChanged(
    oldOpts: SearchableOption[],
    newOpts: SearchableOption[]
  ): boolean {
    if (oldOpts.length !== newOpts.length) return true;

    for (let i = 0; i < oldOpts.length; i++) {
      if (
        String(oldOpts[i].value) !== String(newOpts[i].value) ||
        oldOpts[i].label !== newOpts[i].label
      ) {
        return true;
      }
    }

    return false;
  }

  get searchTerm(): string {
    return this.searchTermSubject.value;
  }

  set searchTerm(value: string) {
    this.searchTermSubject.next(value ?? '');
  }

  get filteredOptions() {
    const searchValue = this.searchTermSubject.value || '';
    const normalizedSearch = this.normalizeText(searchValue);

    if (!searchValue) {
      return this.options;
    }

    return this.options
      .map((option) => {
        if (option.options && option.options.length > 0) {
          const filteredSubOptions = option.options.filter((subOpt) =>
            this.normalizeText(subOpt.label).includes(normalizedSearch)
          );
          if (filteredSubOptions.length > 0) {
            return {
              ...option,
              options: filteredSubOptions,
              isGroup: true,
            };
          }
          if (this.normalizeText(option.label).includes(normalizedSearch)) {
            return option;
          }
          return null;
        }
        return this.normalizeText(option.label).includes(normalizedSearch) ? option : null;
      })
      .filter((opt) => opt !== null) as SearchableOption[];
  }

  toggleDropdown(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (this.disabled) return;

    this.dropdownService.toggleDropdown(this.dropdownId!);
  }

  clearSearch() {
    this.searchTerm = '';
    this.inputChanged.emit(null);
  }

  onUserInput(event: Event) {
    const v = (event.target as HTMLInputElement).value;
    this.searchTerm = v;
    const trimmed = v == null ? '' : String(v).trim();
    this.inputChanged.emit(trimmed === '' ? null : v);
  }

  selectOption(option: SearchableOption, event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (this.multiSelect) {
      this.toggleOptionSelection(option, event);
      return;
    }

    const exists = this._options.some(
      (o) => String(o.value) === String(option.value)
    );

    if (!exists && !this.allowCreateFromInput) {
      this.onDataChanged.emit(String(option.value));
      this.dropdownService.closeDropdown();
      this.searchTerm = '';
      return;
    }

    this._selectedOption = option;
    this.lastSelectedValue = String(option.value);
    this.pendingValue = null;

    this.onDataChanged.emit(String(option.value));
    this.dropdownService.closeDropdown();
    this.searchTerm = '';

    this.cd.detectChanges();
  }

  toggleOptionSelection(option: SearchableOption, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    const index = this._selectedOptions.findIndex(
      opt => String(opt.value) === String(option.value)
    );

    if (index > -1) {
      this._selectedOptions.splice(index, 1);
    } else {
      this._selectedOptions.push(option);
    }

    this.multiSelectChanged.emit([...this._selectedOptions]);
    this.cd.detectChanges();
  }

  isOptionSelected(option: SearchableOption): boolean {
    if (!this.multiSelect) {
      return String(this._selectedOption?.value) === String(option.value);
    }
    return this._selectedOptions.some(
      opt => String(opt.value) === String(option.value)
    );
  }

  clearAllSelections(): void {
    this._selectedOptions = [];
    this.multiSelectChanged.emit([]);
    this.cd.detectChanges();
  }

  getSelectedCount(): number {
    return this._selectedOptions.length;
  }


  private shouldIgnoreDocumentClick(target: HTMLElement): boolean {
    if (Date.now() - this.dropdownService.getLastOpenedAt() < OPEN_GUARD_MS) {
      return true;
    }
    const clickedInTrigger = this.selectContainer?.nativeElement?.contains(target);
    const clickedInPanel = this.panelRef?.nativeElement?.contains(target);
    return !!(clickedInTrigger || clickedInPanel);
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    if (!this.isOpen()) return;
    if (this.autoOpen && Date.now() - this.initTime < 200) {
      return;
    }

    const target = event.target as HTMLElement;

    if (this.shouldIgnoreDocumentClick(target)) return;
    if (this.multiSelect) {
      const clickedInTable = target.closest('table');
      if (!clickedInTable) {
        this.dropdownService.closeDropdown();
      }
    } else {
      this.dropdownService.closeDropdown();
    }
  }

  @HostListener('document:mousedown', ['$event'])
  onMouseDownOutside(event: MouseEvent) {
    if (!this.isOpen()) return;
    if (this.shouldIgnoreDocumentClick(event.target as HTMLElement)) return;
    if (!this.multiSelect) {
      this.dropdownService.closeDropdown();
    }
  }

  @HostListener('document:touchstart', ['$event'])
  onTouchStartOutside(event: TouchEvent) {
    if (!this.isOpen()) return;
    if (this.shouldIgnoreDocumentClick(event.target as HTMLElement)) return;
    if (!this.multiSelect) {
      this.dropdownService.closeDropdown();
    }
  }

  private findScrollableAncestors(element: HTMLElement): HTMLElement[] {
    const ancestors: HTMLElement[] = [];
    let parent = element.parentElement;

    while (parent) {
      const hasOverflow = this.hasScrollableOverflow(parent);
      if (hasOverflow) {
        ancestors.push(parent);
      }
      parent = parent.parentElement;
    }

    return ancestors;
  }

  private hasScrollableOverflow(element: HTMLElement): boolean {
    const style = window.getComputedStyle(element);
    const overflowX = style.overflowX;
    const overflowY = style.overflowY;

    return (
      (overflowX === 'scroll' ||
        overflowX === 'auto' ||
        overflowY === 'scroll' ||
        overflowY === 'auto') &&
      (element.scrollHeight > element.clientHeight ||
        element.scrollWidth > element.clientWidth)
    );
  }

  private openPanel() {
  if (!this.panelRef) return;
  const panelEl = this.panelRef.nativeElement;

  if (this.attachToBody) {
    if (this.moved) {
      this.positionPanel();
      return;
    }
    const parent = panelEl.parentNode as Node;
    if (!parent) return;
    this.placeholderNode = document.createComment('dropdown-placeholder');
    parent.replaceChild(this.placeholderNode, panelEl);
    document.body.appendChild(panelEl);
    panelEl.classList.add('searchable-dropdown-panel');
    panelEl.style.position = 'fixed';
    panelEl.style.zIndex = '11000';
    const triggerWidthPx = (this.selectContainer?.nativeElement as HTMLElement)?.getBoundingClientRect().width;
    const initialWidth = this.panelWidth != null ? this.panelWidth : `${triggerWidthPx}px`;
    panelEl.style.width = initialWidth;
    panelEl.style.minWidth = initialWidth;
    panelEl.style.maxWidth = 'none';
    this.moved = true;
    this.scrollableAncestors = this.findScrollableAncestors(this.selectContainer?.nativeElement as HTMLElement);
    window.addEventListener('resize', this.onWindowResize);
    window.addEventListener('scroll', this.onWindowScroll, true);
    this.scrollableAncestors.forEach(ancestor => ancestor.addEventListener('scroll', this.onAncestorScroll));
    if (!this.resizeObserver && this.selectContainer?.nativeElement) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.moved && this.isOpen()) {
          this.positionPanel();
        }
      });
      this.resizeObserver.observe(this.selectContainer.nativeElement);
    }
    
    if (!this.layoutObserver) {
      this.layoutObserver = new ResizeObserver(() => {
        if (this.moved && this.isOpen()) {
          this.positionPanel();
        }
      });
      this.layoutObserver.observe(document.body);
    }
    
    this.positionPanel();
  } else {
    panelEl.style.position = 'absolute';
    panelEl.style.zIndex = '50';
    panelEl.style.width = 'auto';
    panelEl.style.minWidth = '216px';
    panelEl.style.maxHeight = '212px';
    panelEl.style.overflowY = 'auto';
    panelEl.classList.add('searchable-dropdown-panel');
  }
  this.cd.detectChanges();
}

  private closePanel() {
    if (!this.panelRef) return;
    const panelEl = this.panelRef.nativeElement;
    if (!panelEl) return;
    if (!this.moved) return;
     if (!this.attachToBody) {

    panelEl.style.position = '';
    panelEl.style.top = '';
    panelEl.style.left = '';
    panelEl.style.zIndex = '';
    panelEl.style.width = '';
    panelEl.classList.remove('searchable-dropdown-panel');
 return;
  }
  
    // Limpiar ResizeObservers cuando se cierra el panel
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
    
    if (this.layoutObserver) {
      this.layoutObserver.disconnect();
      this.layoutObserver = undefined;
    }
    
    if (this.placeholderNode && this.placeholderNode.parentNode) {
      this.placeholderNode.parentNode.replaceChild(
        panelEl,
        this.placeholderNode
      );
    }

    this.placeholderNode = null;
    this.moved = false;
    this.cd.detectChanges();
  }

  private positionPanel() {
    try {
      if (!this.moved || !this.panelRef || !this.selectContainer) return;

      const panelEl = this.panelRef.nativeElement as HTMLElement;
      const triggerEl = this.selectContainer.nativeElement as HTMLElement;
      const triggerRect = triggerEl.getBoundingClientRect();

      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const maxPanelHeight = 212;
      const gap = 4;

      let top: number;
      let left: number;

      if (this.autoOpen) {
        top = triggerRect.top;
        left = triggerRect.left;
      } else {
        top = triggerRect.bottom + gap;
        left = triggerRect.left;
      }

      let adjustedMaxHeight = maxPanelHeight;

      if (spaceBelow < maxPanelHeight + gap) {
        adjustedMaxHeight = Math.max(150, spaceBelow - gap - 16);
      }

      panelEl.style.top = `${top}px`;
      panelEl.style.left = `${left}px`;
      if (this.panelWidth == null) {
        panelEl.style.width = `${triggerRect.width}px`;
        panelEl.style.minWidth = `${triggerRect.width}px`;
        panelEl.style.maxWidth = 'none';
      } else {
        panelEl.style.minWidth = `${Math.max(triggerRect.width, 216)}px`;
      }
      panelEl.style.maxHeight = `${adjustedMaxHeight}px`;

      panelEl.style.overflowY = 'hidden';
    } catch (e) {
      console.error('Error positioning panel:', e);
    }
  }

  private normalizeText(text: string): string {
    if (!text) return '';
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  ngOnDestroy(): void {
    try {
      this.searchTermSubject.complete();
    } catch (e) {
      console.error('Error completing search term subject:', e);
    }

    // Limpiar ResizeObservers
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
    
    if (this.layoutObserver) {
      this.layoutObserver.disconnect();
      this.layoutObserver = undefined;
    }

    this.closePanel();
  }
}